import {
  ArrowPathIcon,
  CheckBadgeIcon,
  FireIcon,
  TrophyIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import GradientSpectrum from './GradientSpectrum';
import type { ComponentType, ReactNode, SVGProps } from 'react';
import { useState } from 'react';
import type {
  PhaseOneExtractionResponse,
  PhaseTwoAnalysisResponse,
  PhaseTwoMetadataRequest
} from '../types';
import { format } from 'date-fns';
import clsx from 'clsx';

type PlacementTier =
  | 'Neighborhood Bar'
  | 'Standard Cocktail Lounge'
  | 'Festival / Ticketed Event'
  | 'Stadium / Arena Pricing'
  | 'Ultra-Premium / VIP Event';

const SPECTRUM_STEPS: PlacementTier[] = [
  'Neighborhood Bar',
  'Standard Cocktail Lounge',
  'Festival / Ticketed Event',
  'Stadium / Arena Pricing',
  'Ultra-Premium / VIP Event'
];

// Display labels equal identifiers for consistency across API and UI
const TIER_LABELS: Record<PlacementTier, string> = {
  'Neighborhood Bar': 'Neighborhood Bar',
  'Standard Cocktail Lounge': 'Standard Cocktail Lounge',
  'Festival / Ticketed Event': 'Festival / Ticketed Event',
  'Stadium / Arena Pricing': 'Stadium / Arena Pricing',
  'Ultra-Premium / VIP Event': 'Ultra-Premium / VIP Event'
};

const normalizeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

interface Props {
  extraction?: PhaseOneExtractionResponse | null;
  analysis?: PhaseTwoAnalysisResponse | null;
  metadata?: Partial<PhaseTwoMetadataRequest> | null;
  isLoading?: boolean;
}

const formatCurrency = (value: number) => `$${value.toFixed(2)}`;

const AnalysisReport = ({ extraction, analysis, metadata, isLoading }: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-white/70 p-10 text-center shadow-xl backdrop-blur">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-slate-500" />
        <p className="text-sm text-slate-600">Crunching your menu data...</p>
      </div>
    );
  }

  if (!analysis) {
    return null;
  }

  const comparative = analysis.comparativeData;
  const hasCategoryAnalysis = analysis.categoryAnalysis.length > 0;
  const hasRecommendations = analysis.recommendations.length > 0;
  const placement = analysis.overallAnalysis.pricingSpectrumPlacement;
  const eventDateValue = metadata?.eventDate ?? extraction?.menu?.eventDate ?? null;
  const parsedEventDate = eventDateValue ? new Date(eventDateValue) : null;
  const formattedEventDate =
    parsedEventDate && !Number.isNaN(parsedEventDate.getTime()) ? format(parsedEventDate, 'PPP') : 'Not provided';
  const eventName = (metadata as any)?.eventName ?? (extraction as any)?.menu?.eventName ?? 'Not provided';
  const eventLocation = metadata?.eventLocation ?? extraction?.menu?.eventLocation ?? 'Not provided';
  const eventType = metadata?.eventType ?? extraction?.menu?.eventType ?? 'Not provided';
  const taxValue = metadata?.taxIncluded ?? extraction?.menu?.taxIncluded ?? null;
  const serviceValue = metadata?.serviceFeeIncluded ?? extraction?.menu?.serviceFeeIncluded ?? null;
  const mapApplicability = (v: any) => v === 'NotApplicable' ? 'Not applicable' : (v ?? 'Not provided');
  const taxIncluded = mapApplicability(taxValue);
  const serviceFeeIncluded = mapApplicability(serviceValue);
  const includesCopy =
    taxIncluded === 'Not provided' && serviceFeeIncluded === 'Not provided'
      ? 'Not provided'
      : `Tax: ${taxIncluded} · Service: ${serviceFeeIncluded}`;

  const comparativeLookup = new Map<string, string[]>();
  if (comparative) {
    comparative.rows.forEach((row) => {
      if (!row?.length) {
        return;
      }
      comparativeLookup.set(normalizeKey(row[0]), row.slice(1));
    });
  }

  const yourMenuRow = comparative?.rows.find((row) => normalizeKey(row?.[0] ?? '') === 'your menu') ?? null;
  const yourMenuMetrics = yourMenuRow ? yourMenuRow.slice(1).filter(Boolean) : [];
  const yourMenuSummary = yourMenuMetrics.length > 0 ? `Your menu: ${yourMenuMetrics.join(' · ')}` : null;

  const categoriesList = extraction?.menu?.menuData?.categories ?? [];
  // Prefer counts from extraction when available; otherwise fall back to server-provided breakdown or analysis summary
  const categoriesCount = Array.isArray(categoriesList) && categoriesList.length > 0
    ? categoriesList.length
    : Array.isArray(analysis.currentMenuBreakdown) && analysis.currentMenuBreakdown.length > 0
      ? analysis.currentMenuBreakdown.length
      : Array.isArray(analysis.categoryAnalysis) ? analysis.categoryAnalysis.length : 0;

  const itemsCount = Array.isArray(categoriesList) && categoriesList.length > 0
    ? categoriesList.reduce((sum, c) => sum + (c?.products?.length ?? 0), 0)
    : Array.isArray(analysis.currentMenuBreakdown) && analysis.currentMenuBreakdown.length > 0
      // Count only items with known basePrice to avoid implying inferred prices
      ? analysis.currentMenuBreakdown.reduce((sum, cat) => sum + (Array.isArray(cat.items) ? cat.items.filter(it => it.basePrice != null).length : 0), 0)
      : 0;

  return (
    <div className="space-y-10">
      <section
        className="group relative overflow-hidden rounded-3xl p-[1px] shadow-[0_50px_120px_-60px_rgba(15,23,42,0.15)]"
        style={{
          backgroundImage:
            'linear-gradient(135deg, color-mix(in oklch, var(--color-primary) 20%, transparent), transparent 60%), linear-gradient(225deg, color-mix(in oklch, color-mix(in oklch, var(--color-primary) 65%, var(--color-accent)) 20%, transparent), transparent 60%)'
          }}
      >
        <div className="relative rounded-[26px] border border-slate-200 bg-white/70 p-8 backdrop-blur-xl">
          <div className="pointer-events-none absolute -inset-px opacity-60 mix-blend-screen">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.25),transparent_55%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(249,115,22,0.3),transparent_45%)]" />
          </div>
          <div className="relative z-10 flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-5 text-left">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-5 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-700">
                Overall positioning
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-slate-500">Pricing spectrum</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl">
                  {analysis.overallAnalysis.pricingSpectrumPlacement.replace(/([A-Z])/g, ' $1').trim()}
                </h2>
              </div>
              <p className="max-w-2xl text-base text-slate-600">{analysis.overallAnalysis.summary}</p>
            </div>
            <div className="relative w-full max-w-xs overflow-hidden rounded-2xl border border-slate-200 bg-white/70 p-6 text-right shadow-sm">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                      'linear-gradient(135deg, color-mix(in oklch, var(--color-primary) 20%, transparent), transparent 60%), linear-gradient(225deg, color-mix(in oklch, color-mix(in oklch, var(--color-primary) 65%, var(--color-accent)) 20%, transparent), transparent 60%)'
                  }}
              />
              <div className="relative z-10 flex flex-col items-end gap-2 text-sm text-slate-700">
                <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Overall score</span>
                <span className="text-4xl font-semibold text-slate-900">
                  {analysis.overallAnalysis.overallScore.toFixed(1)}
                </span>
                <span className="text-xs text-slate-500">out of 100</span>
                <p className="mt-2 text-[11px] leading-snug text-right text-slate-600">
                  Indicates how well your menu’s pricing aligns to the selected spectrum tier across categories and out-the-door policies. Higher is better.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <GradientSpectrum
              tiers={SPECTRUM_STEPS.map((t) => ({ id: t, label: TIER_LABELS[t] }))}
              placementId={placement}
              tooltip={yourMenuSummary}
            />
          </div>
          <div className="relative z-10 mt-8 grid gap-4 text-sm md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white/70 p-5">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Event summary</p>
              <div className="mt-3 space-y-1 text-slate-800">
                <div><span className="text-slate-500">Name:</span> {eventName}</div>
                <div><span className="text-slate-500">Type:</span> {eventType}</div>
                <div><span className="text-slate-500">Location:</span> {eventLocation}</div>
                <div><span className="text-slate-500">Date:</span> {formattedEventDate}</div>
                <div><span className="text-slate-500">Includes:</span> {includesCopy}</div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white/70 p-5">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Summary of info gathered</p>
              <div className="mt-3 grid grid-cols-2 gap-4 text-slate-800">
                <div>
                  <p className="text-3xl font-semibold text-slate-900">{categoriesCount}</p>
                  <p className="text-xs text-slate-500">Categories</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-slate-900">{itemsCount}</p>
                  <p className="text-xs text-slate-500">Items detected</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-600">Overall score: {analysis.overallAnalysis.overallScore.toFixed(1)} / 100</p>
            </div>
          </div>
          {/* Full menu moved to dedicated section below */}
        </div>
      </section>

      {hasCategoryAnalysis && (
        <Section title="Category breakdown" icon={TrophyIcon}>
          <div className="grid gap-5 md:grid-cols-2">
            {analysis.categoryAnalysis.map((entry) => (
              <div
                key={entry.category}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white/70 p-6 shadow-sm"
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.15),transparent_60%)]" />
                <div className="relative z-10">
                  <p className="text-base font-semibold text-slate-900">{entry.category}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.35em]" style={{ color: 'var(--color-primary)' }}>{entry.ranking}</p>
                  <dl className="mt-5 space-y-3 text-sm text-slate-700">
                    <div className="flex justify-between">
                      <dt>Average price</dt>
                      <dd className="font-semibold text-slate-900">{formatCurrency(entry.averagePrice)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt>Range</dt>
                      <dd>
                        {formatCurrency(entry.priceRange.min)} – {formatCurrency(entry.priceRange.max)}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-4 text-xs text-slate-600">{entry.comparisonNotes}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      

      <Section title="Event & Venue Beverage Pricing Spectrum" icon={CheckBadgeIcon}>
        <div className="relative mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white/70">
          {comparative ? (
            <table className="min-w-[960px] text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.25em] text-slate-600">
                <tr>
                  {comparative.headers.map((h, i) => (
                    <th key={`h-${i}`} className={clsx('px-4 py-3 text-left', i === comparative.headers.length - 1 && 'font-semibold')}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {comparative.rows.map((row, ri) => (
                  <tr key={`r-${ri}`} className="odd:bg-white even:bg-slate-50">
                    {row.map((cell, ci) => (
                      <td key={`c-${ri}-${ci}`} className={clsx('px-4 py-4', ci === 0 ? 'font-semibold text-slate-900' : 'text-slate-700')}>
                        {cell ?? '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-2 py-4 text-sm text-slate-600">Comparative data not available.</p>
          )}
        </div>
      </Section>

      {/* Current Menu section */}
      {extraction?.menu?.menuData?.categories?.length ? (
        <Section title="Current menu" icon={CheckBadgeIcon}>
          {(() => {
            // Determine effective rates and inclusion
            const svcIncluded = (metadata?.serviceFeeIncluded ?? extraction?.menu?.serviceFeeIncluded) as
              | 'Included'
              | 'Additional'
              | 'NotApplicable'
              | undefined;
            const taxIncludedVal = (metadata?.taxIncluded ?? extraction?.menu?.taxIncluded) as
              | 'Included'
              | 'Additional'
              | 'NotApplicable'
              | undefined;
            const hideSvc = svcIncluded === 'NotApplicable';
            const hideTax = taxIncludedVal === 'NotApplicable';
            const hideTotal = hideSvc && hideTax;

            const estimateRates = (loc: string | undefined | null) => {
              const l = (loc ?? '').toLowerCase();
              // very light heuristics; defaults reflect common event assumptions
              if (l.includes('seattle') || l.includes(', wa') || l.includes('washington')) {
                return { serviceFeeRate: 20, taxRate: 10.35 };
              }
              if (l.includes('new york') || l.includes(', ny')) {
                return { serviceFeeRate: 20, taxRate: 8.875 };
              }
              if (l.includes('los angeles') || l.includes(', ca') || l.includes('california')) {
                return { serviceFeeRate: 20, taxRate: 9.5 };
              }
              return { serviceFeeRate: (metadata as any)?.serviceFeeRate ?? 20, taxRate: (metadata as any)?.taxRate ?? 10.35 };
            };

            const { serviceFeeRate, taxRate } = estimateRates(
              metadata?.eventLocation ?? extraction?.menu?.eventLocation
            );

            const computeTotals = (base: number | null | undefined) => {
              if (base == null) return { svc: null, tax: null, total: null } as const;
              const fee = svcIncluded === 'Additional' ? (base * serviceFeeRate) / 100 : 0;
              const taxBase = taxIncludedVal === 'Additional' ? base + fee : 0;
              const taxAmt = taxIncludedVal === 'Additional' ? (taxBase * taxRate) / 100 : 0;
              const total = base + fee + taxAmt;
              return { svc: fee, tax: taxAmt, total } as const;
            };

            const placementCopy = `About average for the ${TIER_LABELS[placement as PlacementTier]}`;

            // Prefer server-provided breakdown if available
            const serverCats = analysis.currentMenuBreakdown;

            return (
              <div className="space-y-8">
                {(serverCats ?? []).length > 0
                  ? serverCats!.map((category) => (
                      <div key={category.category}>
                        <h4 className="mb-3 text-lg font-semibold text-slate-900">{category.category}</h4>
                        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white/70">
                          <table className="min-w-full text-sm">
                            <colgroup>
                              <col style={{ width: '30%' }} />
                              {/* Current price */}
                              <col style={{ width: hideTotal ? '42%' : (hideSvc ? (!hideTax ? '21%' : '42%') : (hideTax ? '21%' : '14%')) }} />
                              {/* Service charge (only if shown) */}
                              {hideSvc ? null : <col style={{ width: hideTax ? '21%' : '14%' }} />}
                              {/* Tax (only if shown) */}
                              {hideTax ? null : <col style={{ width: hideSvc ? '21%' : '14%' }} />}
                              {/* Current total (only if shown) */}
                              {hideTotal ? null : <col style={{ width: '14%' }} />}
                              {/* Placement */}
                              <col style={{ width: hideTotal ? '28%' : '14%' }} />
                            </colgroup>
                            <thead className="bg-slate-50 text-xs uppercase tracking-[0.25em] text-slate-600">
                              <tr>
                                <th className="px-4 py-3 text-left">Product</th>
                                <th className="px-4 py-3 text-right">Current price</th>
                                {hideSvc ? null : <th className="px-4 py-3 text-right">Service charge</th>}
                                {hideTax ? null : <th className="px-4 py-3 text-right">Tax</th>}
                                {hideTotal ? null : <th className="px-4 py-3 text-right">Current total</th>}
                                <th className="px-4 py-3 text-left">Placement on spectrum</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {category.items.map((p, idx) => (
                                <tr key={`${category.category}-${idx}`} className="odd:bg-white even:bg-slate-50">
                                  <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                                  <td className="px-4 py-3 text-right">{p.basePrice != null ? formatCurrency(p.basePrice) : '-'}</td>
                                  {hideSvc ? null : (
                                    <td className="px-4 py-3 text-right">{p.serviceCharge != null ? formatCurrency(p.serviceCharge) : p.basePrice != null ? 'Included' : '-'}</td>
                                  )}
                                  {hideTax ? null : (
                                    <td className="px-4 py-3 text-right">{p.tax != null ? formatCurrency(p.tax) : p.basePrice != null ? 'Included' : '-'}</td>
                                  )}
                                  {hideTotal ? null : (
                                    <td className="px-4 py-3 text-right">{p.total != null ? formatCurrency(p.total) : '-'}</td>
                                  )}
                                  <td className="px-4 py-3 text-left text-slate-700">{p.basePrice != null ? (p.placementNote ?? '-') : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  : extraction.menu.menuData.categories.map((category) => (
                  <div key={category.categoryName}>
                    <h4 className="mb-3 text-lg font-semibold text-slate-900">{category.categoryName}</h4>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white/70">
                      <table className="min-w-full text-sm">
                        <colgroup>
                          <col style={{ width: '30%' }} />
                          {/* Current price */}
                          <col style={{ width: hideTotal ? '42%' : (hideSvc ? (!hideTax ? '21%' : '42%') : (hideTax ? '21%' : '14%')) }} />
                          {/* Service charge (only if shown) */}
                          {hideSvc ? null : <col style={{ width: hideTax ? '21%' : '14%' }} />}
                          {/* Tax (only if shown) */}
                          {hideTax ? null : <col style={{ width: hideSvc ? '21%' : '14%' }} />}
                          {/* Current total (only if shown) */}
                          {hideTotal ? null : <col style={{ width: '14%' }} />}
                          {/* Placement */}
                          <col style={{ width: hideTotal ? '28%' : '14%' }} />
                        </colgroup>
                        <thead className="bg-slate-50 text-xs uppercase tracking-[0.25em] text-slate-600">
                          <tr>
                            <th className="px-4 py-3 text-left">Product</th>
                            <th className="px-4 py-3 text-right">Current price</th>
                            {hideSvc ? null : (
                              <th className="px-4 py-3 text-right">Service charge ({serviceFeeRate.toFixed(2)}%)</th>
                            )}
                            {hideTax ? null : (
                              <th className="px-4 py-3 text-right">Tax ({taxRate.toFixed(2)}%)</th>
                            )}
                            {hideTotal ? null : <th className="px-4 py-3 text-right">Current total</th>}
                            <th className="px-4 py-3 text-left">Placement on spectrum</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {category.products.map((p) => {
                            const totals = computeTotals(p.price);
                            return (
                              <tr key={`${category.categoryName}-${p.name}`} className="odd:bg-white even:bg-slate-50">
                                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                                <td className="px-4 py-3 text-right">{p.price != null ? formatCurrency(p.price) : '-'}</td>
                                    {hideSvc ? null : (
                                      <td className="px-4 py-3 text-right">
                                        {svcIncluded === 'Included' && p.price != null ? 'Included' : totals.svc != null ? formatCurrency(totals.svc) : p.price != null ? 'Included' : '-'}
                                      </td>
                                    )}
                                    {hideTax ? null : (
                                      <td className="px-4 py-3 text-right">
                                        {taxIncludedVal === 'Included' && p.price != null ? 'Included' : totals.tax != null ? formatCurrency(totals.tax) : p.price != null ? 'Included' : '-'}
                                      </td>
                                    )}
                                {hideTotal ? null : (
                                  <td className="px-4 py-3 text-right">{totals.total != null ? formatCurrency(totals.total) : '-'}</td>
                                )}
                                <td className="px-4 py-3 text-left text-slate-700">{p.price != null ? placementCopy : '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-slate-500">
                  Totals assume service fees apply before tax. If rates are not provided, typical regional assumptions are used.
                </p>
              </div>
            );
          })()}
        </Section>
      ) : null}

      {(analysis.detailedRecommendations?.length ?? 0) > 0 ? (
        <Section title="Revenue opportunities" icon={FireIcon}>
          <div className="space-y-8">
            {analysis.detailedRecommendations!.map((cat, ci) => (
              <div key={`${cat.category}-${ci}`}>
                <h4 className="mb-3 text-lg font-semibold text-slate-900">{cat.category}</h4>
                <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white/70">
                  <table className="min-w-full text-sm">
                    <colgroup>
                      <col style={{ width: '30%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '34%' }} />
                    </colgroup>
                    <thead className="bg-slate-50 text-xs uppercase tracking-[0.25em] text-slate-600">
                      <tr>
                        <th className="px-4 py-3 text-left">Product</th>
                        <th className="px-4 py-3 text-right">Recommended price</th>
                        <th className="px-4 py-3 text-left">Change</th>
                        <th className="px-4 py-3 text-left">Why</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {cat.items.map((it, ri) => {
                        const delta = it.changeDelta ?? 0;
                        const pct = it.changePercent ?? null;
                        const up = delta > 0;
                        return (
                          <tr key={`${cat.category}-${ri}`} className="odd:bg-white even:bg-slate-50">
                            <td className="px-4 py-3 font-medium text-slate-900">{it.product}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(it.recommendedPrice)}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 ${up ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-slate-600'}`}>
                                {delta !== 0 ? (up ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />) : null}
                                {delta === 0 ? 'No change' : `${formatCurrency(Math.abs(delta))}${pct != null ? ` (${(Math.abs(pct)).toFixed(1)}%)` : ''}`}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-700">{it.why}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
          {(analysis.additionalRecommendationCards?.length ?? 0) > 0 && (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {analysis.additionalRecommendationCards!.map((card, i) => {
                const isLast = i === analysis.additionalRecommendationCards!.length - 1;
                const isOddCount = (analysis.additionalRecommendationCards!.length % 2) === 1;
                const spanClass = isLast && isOddCount ? 'md:col-span-2' : '';
                return (
                  <div key={`addl-card-${i}`} className={`rounded-2xl border border-slate-200 bg-white/70 p-5 ${spanClass}`}>
                    <p className="text-sm font-semibold text-slate-900">{card.title}</p>
                    <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-700">
                      {card.items.map((text, j) => (
                        <li key={`addl-card-${i}-${j}`}>{text}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      ) : (
        hasRecommendations && (
          <Section title="Revenue opportunities" icon={FireIcon}>
            <div className="grid gap-5 md:grid-cols-2">
              {analysis.recommendations.map((item, index) => (
                <div
                  key={`${item.itemCategory}-${index}`}
                  className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white/70 p-6 text-sm text-slate-900 shadow-sm"
                >
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(250,204,21,0.15),transparent_60%)]" />
                  <div className="relative z-10 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-600">{item.type}</p>
                    <p className="text-2xl font-semibold text-slate-900">{item.itemCategory}</p>
                    <p className="text-xs text-slate-600">
                      Current range {item.currentPriceRange} • Suggested {item.suggestedPriceRange}
                    </p>
                    <p className="text-xs text-slate-600">{item.rationale}</p>
                    <p className="text-xs font-semibold">{item.potentialRevenueImpact}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )
      )}

      {analysis.impact && (
        <Section title="Estimated Impact" icon={TrophyIcon}>
          <div className="mb-3 text-sm text-slate-600">
            Estimated upside if recommended changes are applied.
          </div>
          {(() => {
            const imp = analysis.impact!;
            const currency = imp.currency ?? '';
            const money = (v: number) =>
              currency
                ? `${currency}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const percent = (v: number) => `${v.toFixed(1)}%`;
            const isPositive = (v: number) => v >= 0;
            return (
              <>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-white/70 p-5">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Uplift</p>
                    <p className={`mt-2 text-3xl font-semibold ${isPositive(imp.upliftPercent) ? 'text-green-600' : 'text-red-600'}`}>
                      {isPositive(imp.upliftPercent) ? '+' : ''}
                      {percent(imp.upliftPercent)}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">Overall projected increase</p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white/70 p-5">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Per‑Attendee (OTD)</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">
                      {money(imp.perCapBase)} <span className="text-slate-400">→</span> {money(imp.perCapNew)}
                    </p>
                    <p className={`mt-1 text-sm ${isPositive(imp.perCapDelta) ? 'text-green-600' : 'text-red-600'}`}>
                      {isPositive(imp.perCapDelta) ? '+' : ''}
                      {money(Math.abs(imp.perCapDelta))}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">Average out‑the‑door spend per attendee, including tax/fees</p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white/70 p-5">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Revenue</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">
                      {money(imp.revenueBase)} <span className="text-slate-400">→</span> {money(imp.revenueNew)}
                    </p>
                    <p className={`mt-1 text-sm ${isPositive(imp.revenueDelta) ? 'text-green-600' : 'text-red-600'}`}>
                      {isPositive(imp.revenueDelta) ? '+' : ''}
                      {money(Math.abs(imp.revenueDelta))}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">Based on attendees</p>
                  </div>
                </div>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white/70 p-5">
                  <p className="text-sm font-semibold text-slate-900">Assumptions</p>
                  <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    <li>
                      Attendees used: {imp.estimatedAttendeesUsed.toLocaleString()}
                    </li>
                    <li>
                      Items per attendee: {imp.itemsPerAttendeeConservative.toFixed(1)}–{imp.itemsPerAttendeeOptimistic.toFixed(1)}
                    </li>
                    {imp.assumptionsNote && imp.assumptionsNote.trim().length > 0 ? (
                      <li>{imp.assumptionsNote}</li>
                    ) : null}
                  </ul>
                </div>
              </>
            );
          })()}
        </Section>
      )}
    </div>
  );
};

interface SectionProps {
  title: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  children: ReactNode;
}

const Section = ({ title, icon: Icon, children }: SectionProps) => (
  <section className="group relative overflow-hidden rounded-3xl theme-card p-8 shadow-sm backdrop-blur-xl">
    <div
      className="pointer-events-none absolute inset-0 opacity-80 transition-opacity duration-500 group-hover:opacity-100"
      style={{
        backgroundImage:
          'linear-gradient(135deg, color-mix(in oklch, var(--color-primary) 20%, transparent), transparent 60%), linear-gradient(225deg, color-mix(in oklch, color-mix(in oklch, var(--color-primary) 65%, var(--color-accent)) 20%, transparent), transparent 60%)'
      }}
    />
    <div className="relative z-10">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: 'var(--color-base-200)', color: 'var(--color-primary)' }}>
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="text-xl font-semibold" style={{ color: 'var(--color-base-content)' }}>{title}</h3>
      </div>
      {children}
    </div>
  </section>
);

export default AnalysisReport;
